import { state, api, $, $$, toast, escHtml } from './core';
import type { EmIstoric, EmPunct, EmStare } from './types';

/** o curbă a unui parametru (o cheie de senzor) */
interface EmSerie { key: string; label: string; color: string; }
/** un parametru al monitorului: cheile cerute, unitatea, curbele; `cumulative` = bare pe interval (energie) */
interface EmParam { keys: string; label: string; unit: string; cumulative?: boolean; datasets: EmSerie[]; }
/** /api/stats/*: numărători pe o coloană (routes/other.py) */
interface Numar { cnt: number; }
/** /api/stats/mentenanta — seriile lunare (routes/other.py:api_stats_mentenanta) și orele liniilor */
interface StatLuna { luna: string; }
interface StatMentenanta {
    pe_luna: (StatLuna & { urgente: number; planificate: number; mttr_min: number | null })[];
    top_masini_urgente: (Numar & { machine_id: string })[];
    alarme_pe_luna: (StatLuna & { total: number; critice: number })[];
    legate_pe_luna: (StatLuna & { legate: number; libere: number })[];
    pov_pe_luna: (StatLuna & { filmari: number })[];
    ore_linii: { id: string; ore: number | null }[];
}
/** setul de date al unui grafic suprapus */
interface SerieGrafic { label: string; data: number[]; bg: string; }

/* ═══ ENERGY MONITORING SYSTEM ════════════════════════════════ */

export var EM_PARAMS: Record<string, EmParam> = {
    power:   { keys: 'power,power_l1,power_l2,power_l3', label: 'Putere activă', unit: 'kW',
               datasets: [
                   { key: 'power', label: 'P total', color: '#00c8ff' },
                   { key: 'power_l1', label: 'P L1', color: '#ff6b1a' },
                   { key: 'power_l2', label: 'P L2', color: '#00e676' },
                   { key: 'power_l3', label: 'P L3', color: '#a78bfa' },
               ] },
    voltage: { keys: 'voltage_l1n,voltage_l2n,voltage_l3n', label: 'Tensiuni L-N', unit: 'V',
               datasets: [
                   { key: 'voltage_l1n', label: 'U L1', color: '#ff6b1a' },
                   { key: 'voltage_l2n', label: 'U L2', color: '#00e676' },
                   { key: 'voltage_l3n', label: 'U L3', color: '#a78bfa' },
               ] },
    current: { keys: 'current_l1,current_l2,current_l3', label: 'Curenți', unit: 'A',
               datasets: [
                   { key: 'current_l1', label: 'I L1', color: '#ff6b1a' },
                   { key: 'current_l2', label: 'I L2', color: '#00e676' },
                   { key: 'current_l3', label: 'I L3', color: '#a78bfa' },
               ] },
    pf:      { keys: 'pf_total,pf_l1,pf_l2,pf_l3', label: 'Factor putere', unit: '',
               datasets: [
                   { key: 'pf_total', label: 'PF total', color: '#00c8ff' },
                   { key: 'pf_l1', label: 'PF L1', color: '#ff6b1a' },
                   { key: 'pf_l2', label: 'PF L2', color: '#00e676' },
                   { key: 'pf_l3', label: 'PF L3', color: '#a78bfa' },
               ] },
    apparent:{ keys: 'apparent_power,reactive_power', label: 'Putere aparentă / reactivă', unit: 'kVA / kvar',
               datasets: [
                   { key: 'apparent_power', label: 'S aparentă', color: '#ffd600' },
                   { key: 'reactive_power', label: 'Q reactivă', color: '#ec4899' },
               ] },
    energy:  { keys: 'consumption', label: 'Energie', unit: 'kWh', cumulative: true,
               datasets: [
                   { key: 'consumption', label: 'Consum', color: '#00e676' },
               ] },
};

export var EM_PERIODS = [
    { id: '1h',  label: '1 oră' },
    { id: '6h',  label: '6 ore' },
    { id: '12h', label: '12 ore' },
    { id: '24h', label: '24 ore' },
    { id: '7d',  label: '7 zile' },
    { id: '30d', label: '30 zile' },
];

export function _renderEnergyMonitor(el: HTMLElement, d: { machine?: { id: string } | null }) {
    try { return _renderEnergyMonitorInner(el, d); } catch(e) {
        console.error('Energy Monitor render error:', e);
        el.innerHTML = `<div style="padding:1rem;color:#ff6b6b">Eroare la încărcarea monitorului energie: ${(e as Error).message}</div>`;
    }
}
export function _renderEnergyMonitorInner(el: HTMLElement, d: { machine?: { id: string } | null }) {
    const machineId = window._detailLineId || (d.machine && d.machine.id);
    if (!machineId) { el.innerHTML = '<p>Mașină necunoscută</p>'; return; }

    const em = state._em!;
    if (em.machineId !== machineId) {
        em.machineId = machineId;
        em.historyData = null;
    }

    const sv = (key: string) => { const v = getSensorValue(machineId, key); return (v != null && !isNaN(v)) ? v : null; };
    const fmt = (v: string|number|null, dec: number|undefined, unit: string) => v != null ? parseFloat(String(v)).toFixed(dec) + (unit ? ' ' + unit : '') : '—';
    const meterRow = (label: string, val: number | null, unit: string, dec?: number) => `<div class="meter-row"><span class="meter-label">${label}</span><span class="meter-value em-live" data-em-key="${label}">${fmt(val, dec || 2, unit)}</span></div>`;

    const power = sv('power');
    const pf = sv('pf_total');
    const consumption = sv('consumption');
    const frequency = sv('frequency');

    const pfClass = pf != null ? (pf >= 0.95 ? 'em-kpi-good' : pf >= 0.85 ? 'em-kpi-warn' : 'em-kpi-bad') : '';

    const periodBtns = EM_PERIODS.map(p =>
        `<button class="em-period-btn ${em.period === p.id ? 'active' : ''}" data-period="${p.id}">${p.label}</button>`
    ).join('');

    const paramBtns = Object.entries(EM_PARAMS).map(([id, cfg]) =>
        `<button class="em-param-btn ${em.param === id ? 'active' : ''}" data-param="${id}">${cfg.label}</button>`
    ).join('');

    el.innerHTML = `
        <div class="em-dashboard">
            <div class="em-kpi-row">
                <div class="em-kpi-card em-kpi-primary">
                    <div class="em-kpi-icon">⚡</div>
                    <div class="em-kpi-body">
                        <div class="em-kpi-label">Putere activă</div>
                        <div class="em-kpi-value em-live-power">${fmt(power, 2, 'kW')}</div>
                    </div>
                </div>
                <div class="em-kpi-card">
                    <div class="em-kpi-icon">🔋</div>
                    <div class="em-kpi-body">
                        <div class="em-kpi-label">Consum perioadă</div>
                        <div class="em-kpi-value em-live-energy-period">—</div>
                        <div class="em-kpi-sub em-live-energy-index" style="font-size:.7em;opacity:.6">Index: ${fmt(consumption, 1, 'kWh')}</div>
                    </div>
                </div>
                <div class="em-kpi-card ${pfClass}">
                    <div class="em-kpi-icon">📐</div>
                    <div class="em-kpi-body">
                        <div class="em-kpi-label">Factor putere</div>
                        <div class="em-kpi-value em-live-pf">${fmt(pf, 3, '')}</div>
                    </div>
                </div>
                <div class="em-kpi-card">
                    <div class="em-kpi-icon">〰️</div>
                    <div class="em-kpi-body">
                        <div class="em-kpi-label">Frecvență</div>
                        <div class="em-kpi-value em-live-freq">${fmt(frequency, 2, 'Hz')}</div>
                    </div>
                </div>
            </div>

            <div class="em-stats-row" id="em-stats-row">
                <div class="em-stat"><span class="em-stat-label" id="em-stat-avg-label">Medie perioadă</span><span class="em-stat-value" id="em-stat-avg">—</span></div>
                <div class="em-stat"><span class="em-stat-label" id="em-stat-max-label">Vârf maxim</span><span class="em-stat-value em-stat-max" id="em-stat-max">—</span></div>
                <div class="em-stat"><span class="em-stat-label" id="em-stat-min-label">Minim</span><span class="em-stat-value em-stat-min" id="em-stat-min">—</span></div>
                <div class="em-stat"><span class="em-stat-label">Energie perioadă</span><span class="em-stat-value" id="em-stat-energy">—</span></div>
                <div class="em-stat"><span class="em-stat-label">Nr. eșantioane</span><span class="em-stat-value" id="em-stat-samples">—</span></div>
            </div>

            <div class="em-chart-panel">
                <div class="em-chart-toolbar">
                    <div class="em-period-group">${periodBtns}</div>
                    <div class="em-param-group">${paramBtns}</div>
                </div>
                <div class="em-chart-wrap" id="em-chart-wrap">
                    <canvas id="em-main-chart"></canvas>
                    <div class="em-chart-loading" id="em-chart-loading" style="display:none">
                        <div class="em-spinner"></div>Se încarcă datele...
                    </div>
                </div>
            </div>

            <details class="em-live-panel" open>
                <summary class="em-live-summary">Valori instantanee</summary>
                <div class="meter-grid">
                    <div class="meter-section">
                        <div class="meter-section-title">⚡ Tensiuni L-N</div>
                        ${meterRow('U L1', sv('voltage_l1n'), 'V')}
                        ${meterRow('U L2', sv('voltage_l2n'), 'V')}
                        ${meterRow('U L3', sv('voltage_l3n'), 'V')}
                    </div>
                    <div class="meter-section">
                        <div class="meter-section-title">⚡ Tensiuni L-L</div>
                        ${meterRow('U L1-L2', sv('voltage_l1l2'), 'V')}
                        ${meterRow('U L2-L3', sv('voltage_l2l3'), 'V')}
                        ${meterRow('U L3-L1', sv('voltage_l3l1'), 'V')}
                    </div>
                    <div class="meter-section">
                        <div class="meter-section-title">🔌 Curenți</div>
                        ${meterRow('I L1', sv('current_l1'), 'A')}
                        ${meterRow('I L2', sv('current_l2'), 'A')}
                        ${meterRow('I L3', sv('current_l3'), 'A')}
                        ${meterRow('I N', sv('current_n'), 'A')}
                    </div>
                    <div class="meter-section">
                        <div class="meter-section-title">⚙ Putere activă</div>
                        ${meterRow('P L1', sv('power_l1'), 'kW')}
                        ${meterRow('P L2', sv('power_l2'), 'kW')}
                        ${meterRow('P L3', sv('power_l3'), 'kW')}
                        ${meterRow('P total', sv('power'), 'kW')}
                    </div>
                    <div class="meter-section">
                        <div class="meter-section-title">📊 Putere aparentă / reactivă</div>
                        ${meterRow('S total', sv('apparent_power'), 'kVA')}
                        ${meterRow('Q total', sv('reactive_power'), 'kvar')}
                    </div>
                    <div class="meter-section">
                        <div class="meter-section-title">📐 Factor putere</div>
                        ${meterRow('PF L1', sv('pf_l1'), '')}
                        ${meterRow('PF L2', sv('pf_l2'), '')}
                        ${meterRow('PF L3', sv('pf_l3'), '')}
                        ${meterRow('PF total', sv('pf_total'), '')}
                    </div>
                    <div class="meter-section">
                        <div class="meter-section-title">〰️ Frecvență</div>
                        ${meterRow('f', sv('frequency'), 'Hz')}
                    </div>
                    <div class="meter-section">
                        <div class="meter-section-title">🔋 Energie</div>
                        ${meterRow('Index contor', sv('consumption'), 'kWh', 1)}
                    </div>
                </div>
            </details>
        </div>
    `;

    el.querySelectorAll<HTMLElement>('.em-period-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
            state._em!.period = btn.dataset.period as string;
            el.querySelectorAll('.em-period-btn').forEach((b) => b.classList.remove('active'));
            btn.classList.add('active');
            _emLoadHistory(machineId);
        });
    });
    el.querySelectorAll<HTMLElement>('.em-param-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
            state._em!.param = btn.dataset.param as string;
            el.querySelectorAll('.em-param-btn').forEach((b) => b.classList.remove('active'));
            btn.classList.add('active');
            _emLoadHistory(machineId);
        });
    });

    _emLoadHistory(machineId);
}

export async function _emLoadHistory(machineId: string) {
    const em = state._em!;
    const paramCfg = EM_PARAMS[em.param];
    if (!paramCfg) return;

    const loadingEl = document.getElementById('em-chart-loading');
    if (loadingEl) loadingEl.style.display = 'flex';
    em.loading = true;

    try {
        const data: EmIstoric = await api(`/api/telemetry/sensors/history/${encodeURIComponent(machineId)}?keys=${paramCfg.keys}&period=${em.period}`);
        em.historyData = data;
        _emRenderChart(data, paramCfg);
        _emRenderStats(data, paramCfg);
    } catch (e) {
        console.warn('Energy history load error:', e);
    } finally {
        em.loading = false;
        if (loadingEl) loadingEl.style.display = 'none';
    }
}

export function _emFormatTime(isoStr: string|number|Date, period: string) {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return isoStr;
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(d.getUTCMinutes()).padStart(2, '0');
    if (['1h', '6h', '12h', '24h'].includes(period)) return `${hh}:${mm}`;
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${dd}.${mo} ${hh}:${mm}`;
}

export function _emRenderChart(data: EmIstoric, paramCfg: EmParam) {
    const canvas = document.getElementById('em-main-chart');
    if (!canvas || typeof Chart === 'undefined') return;

    destroyChart('em-main-chart');
    const ctx = (canvas as HTMLCanvasElement).getContext('2d');
    const period = state._em!.period;
    const isCumulative = paramCfg.cumulative === true;
    const datasets: Record<string, unknown>[] = [];   // seturile Chart.js (biblioteca vine din CDN, fără tipuri)
    let allLabels: string[] = [];

    paramCfg.datasets.forEach((ds, idx) => {
        const points: EmPunct[] = (data.series && data.series[ds.key]) || [];
        if (points.length > allLabels.length) {
            allLabels = points.map((p) => String(_emFormatTime(p.t, period)));
        }
        const hasMinMax = points.length > 0 && points[0].min !== undefined && points[0].max !== undefined;

        if (isCumulative) {
            datasets.push({
                label: ds.label,
                data: points.map((p) => p.avg),
                borderColor: ds.color,
                backgroundColor: ds.color + '80',
                borderWidth: 1,
                borderRadius: 2,
                type: 'bar',
            });
        } else {
            datasets.push({
                label: ds.label,
                data: points.map((p) => p.avg),
                borderColor: ds.color,
                backgroundColor: idx === 0 ? ds.color + '18' : 'transparent',
                fill: idx === 0,
                tension: 0.3,
                pointRadius: points.length > 200 ? 0 : points.length > 80 ? 1 : 2,
                pointHoverRadius: 4,
                borderWidth: idx === 0 ? 2 : 1.5,
            });

            if (hasMinMax && idx === 0) {
                datasets.push({
                    label: ds.label + ' (min-max)',
                    data: points.map((p) => [p.min, p.max]),
                    borderColor: 'transparent',
                    backgroundColor: ds.color + '0d',
                    fill: true,
                    pointRadius: 0,
                    type: 'line',
                    tension: 0.3,
                });
            }
        }
    });

    const chartType = isCumulative ? 'bar' : 'line';
    const tooltipUnit = isCumulative ? 'kWh' : paramCfg.unit;

    chartInstances['em-main-chart'] = new Chart(ctx, {
        type: chartType,
        data: { labels: allLabels, datasets },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: {
                    display: datasets.length > 1,
                    position: 'top',
                    labels: { boxWidth: 12, padding: 12, usePointStyle: true, font: { size: 11 } }
                },
                tooltip: {
                    backgroundColor: 'rgba(10,15,30,.92)',
                    titleFont: { size: 11 },
                    bodyFont: { size: 11 },
                    padding: 10,
                    callbacks: {
                        label: (ctx: { dataset: { label?: string }; raw: unknown }) => {
                            if (Array.isArray(ctx.raw)) return `${ctx.dataset.label}: ${ctx.raw[0]} – ${ctx.raw[1]} ${tooltipUnit}`;
                            return `${ctx.dataset.label}: ${ctx.raw} ${tooltipUnit}`;
                        }
                    }
                }
            },
            scales: {
                x: {
                    display: true,
                    grid: { color: 'rgba(255,255,255,.04)' },
                    ticks: { maxTicksLimit: 12, maxRotation: 0, font: { size: 10 } }
                },
                y: {
                    display: true,
                    beginAtZero: true,
                    grid: { color: 'rgba(255,255,255,.06)' },
                    title: { display: true, text: tooltipUnit, font: { size: 11 } },
                    ticks: { font: { size: 10 } }
                }
            }
        }
    });
}

export function _emRenderStats(data: EmIstoric, paramCfg: EmParam) {
    const kpis = data.kpis || {};
    const primaryKey = paramCfg.datasets[0]?.key;
    const kpi = kpis[primaryKey];
    const unit = paramCfg.unit;
    const isCumulative = paramCfg.cumulative === true;

    const avgEl = document.getElementById('em-stat-avg');
    const maxEl = document.getElementById('em-stat-max');
    const minEl = document.getElementById('em-stat-min');
    const energyEl = document.getElementById('em-stat-energy');
    const samplesEl = document.getElementById('em-stat-samples');
    const avgLabel = document.getElementById('em-stat-avg-label');
    const maxLabel = document.getElementById('em-stat-max-label');
    const minLabel = document.getElementById('em-stat-min-label');

    if (isCumulative && kpi) {
        if (avgLabel) avgLabel.textContent = 'Consum mediu/interval';
        if (maxLabel) maxLabel.textContent = 'Consum max/interval';
        if (minLabel) minLabel.textContent = 'Consum min/interval';
        if (avgEl) avgEl.textContent = kpi.avg.toFixed(2) + ' kWh';
        if (maxEl) maxEl.textContent = kpi.max.toFixed(2) + ' kWh';
        if (minEl) minEl.textContent = kpi.min.toFixed(2) + ' kWh';
        if (energyEl) energyEl.textContent = kpi.energy_kwh != null ? kpi.energy_kwh.toFixed(2) + ' kWh' : '—';
        if (samplesEl) samplesEl.textContent = kpi.samples.toLocaleString('ro-RO');

        const periodEl = document.querySelector('.em-live-energy-period');
        if (periodEl && kpi.energy_kwh != null) {
            periodEl.textContent = kpi.energy_kwh.toFixed(1) + ' kWh';
        }
    } else if (kpi) {
        if (avgLabel) avgLabel.textContent = 'Medie perioadă';
        if (maxLabel) maxLabel.textContent = 'Vârf maxim';
        if (minLabel) minLabel.textContent = 'Minim';
        if (avgEl) avgEl.textContent = kpi.avg.toFixed(2) + (unit ? ' ' + unit : '');
        if (maxEl) maxEl.textContent = kpi.max.toFixed(2) + (unit ? ' ' + unit : '');
        if (minEl) minEl.textContent = kpi.min.toFixed(2) + (unit ? ' ' + unit : '');
        if (energyEl) energyEl.textContent = kpi.energy_kwh != null ? kpi.energy_kwh.toFixed(2) + ' kWh' : '—';
        if (samplesEl) samplesEl.textContent = kpi.samples.toLocaleString('ro-RO');
    } else {
        if (avgLabel) avgLabel.textContent = 'Medie perioadă';
        if (maxLabel) maxLabel.textContent = 'Vârf maxim';
        if (minLabel) minLabel.textContent = 'Minim';
        [avgEl, maxEl, minEl, energyEl, samplesEl].forEach(e => { if (e) e.textContent = '—'; });
    }

    if (!isCumulative) {
        const powerKpi = kpis['power'];
        const periodEl = document.querySelector('.em-live-energy-period');
        if (periodEl && powerKpi && powerKpi.energy_kwh != null) {
            periodEl.textContent = powerKpi.energy_kwh.toFixed(1) + ' kWh';
        }
    }
}

export function _updateEnergyLiveValues() {
    const machineId = state._em!.machineId;
    if (!machineId) return;

    const sv = (key: string) => { const v = getSensorValue(machineId, key); return (v != null && !isNaN(v)) ? v : null; };
    const fmt = (v: string|number|null, dec: number|undefined, unit: string) => v != null ? parseFloat(String(v)).toFixed(dec) + (unit ? ' ' + unit : '') : '—';

    const power = sv('power');
    const pf = sv('pf_total');

    const pwrEl = document.querySelector('.em-live-power');
    if (pwrEl) pwrEl.textContent = fmt(power, 2, 'kW');
    const enEl = document.querySelector('.em-live-energy-index');
    if (enEl) enEl.textContent = 'Index: ' + fmt(sv('consumption'), 1, 'kWh');
    const pfEl = document.querySelector('.em-live-pf');
    if (pfEl) pfEl.textContent = fmt(pf, 3, '');
    const freqEl = document.querySelector('.em-live-freq');
    if (freqEl) freqEl.textContent = fmt(sv('frequency'), 2, 'Hz');

    const pfCard = pfEl && pfEl.closest('.em-kpi-card');
    if (pfCard && pf != null) {
        pfCard.classList.remove('em-kpi-good', 'em-kpi-warn', 'em-kpi-bad');
        pfCard.classList.add(pf >= 0.95 ? 'em-kpi-good' : pf >= 0.85 ? 'em-kpi-warn' : 'em-kpi-bad');
    }

    const liveMap: Record<string, [string, string, number]> = {   // eticheta → [cheia senzorului, unitatea, zecimale]
        'U L1': ['voltage_l1n', 'V', 2], 'U L2': ['voltage_l2n', 'V', 2], 'U L3': ['voltage_l3n', 'V', 2],
        'U L1-L2': ['voltage_l1l2', 'V', 2], 'U L2-L3': ['voltage_l2l3', 'V', 2], 'U L3-L1': ['voltage_l3l1', 'V', 2],
        'I L1': ['current_l1', 'A', 2], 'I L2': ['current_l2', 'A', 2], 'I L3': ['current_l3', 'A', 2], 'I N': ['current_n', 'A', 2],
        'P L1': ['power_l1', 'kW', 2], 'P L2': ['power_l2', 'kW', 2], 'P L3': ['power_l3', 'kW', 2], 'P total': ['power', 'kW', 2],
        'S total': ['apparent_power', 'kVA', 2], 'Q total': ['reactive_power', 'kvar', 2],
        'PF L1': ['pf_l1', '', 2], 'PF L2': ['pf_l2', '', 2], 'PF L3': ['pf_l3', '', 2], 'PF total': ['pf_total', '', 2],
        'f': ['frequency', 'Hz', 2], 'Index contor': ['consumption', 'kWh', 1],
    };
    document.querySelectorAll('.em-live').forEach(el => {
        const key = (el as HTMLElement).dataset.emKey;
        const cfg = liveMap[key!];
        if (cfg) el.textContent = fmt(sv(cfg[0]), cfg[2], cfg[1]);
    });
}


/* ═══ STATISTICS DASHBOARD ═════════════════════════════════════ */
/** graficele desenate, ca să fie distruse înainte de redesenare (obiecte Chart.js) */
export const chartInstances: Record<string, { destroy(): void }> = {};

export function destroyChart(id: string) {
    if (chartInstances[id]) {
        chartInstances[id].destroy();
        delete chartInstances[id];
    }
}

export const CHART_COLORS = [
    '#00c8ff', '#00e676', '#ff6b1a', '#ff3d3d', '#a78bfa',
    '#06b6d4', '#ffd600', '#ec4899', '#14b8a6', '#f43f5e',
    '#818cf8', '#84cc16', '#c084fc', '#38bdf8', '#d946ef'
];

Chart.defaults.color = '#6b7590';
Chart.defaults.borderColor = 'rgba(255,255,255,.06)';
Chart.defaults.font.family = "'Inter', sans-serif";

export async function loadStatistics() {
    const [
        partsMachine, partsCategory, partsWear,
        errorsMachine, intervalsMachine, machinesType,
        docsType, moldMaterials, stats
    ] = await Promise.all([
        api('/api/stats/parts_by_machine'),
        api('/api/stats/parts_by_category'),
        api('/api/stats/parts_by_wear'),
        api('/api/stats/errors_by_machine'),
        api('/api/stats/intervals_by_machine'),
        api('/api/stats/machines_by_type'),
        api('/api/stats/docs_by_type'),
        api('/api/stats/mold_materials'),
        api('/api/stats')
    ]);

    const sc = $('#dash-summary-cards');
    sc!.innerHTML = `
        <div class="stat-card green"><div class="label">Total Piese</div><div class="value">${stats.parts.toLocaleString()}</div></div>
        <div class="stat-card"><div class="label">Intervale Mentenanță</div><div class="value">${stats.intervals}</div></div>
        <div class="stat-card orange"><div class="label">Coduri Eroare</div><div class="value">${stats.errors}</div></div>
        <div class="stat-card red"><div class="label">Componente Matriță</div><div class="value">${stats.mold_components}</div></div>
        <div class="stat-card green"><div class="label">Documente</div><div class="value">${stats.documents}</div></div>
        <div class="stat-card"><div class="label">Lucrări Jurnal</div><div class="value">${stats.log_entries}</div></div>
    `;

    makeBarChart('chart-parts-machine', partsMachine.map((r: { machine_id: string }) => r.machine_id), partsMachine.map((r: Numar) => r.cnt), 'Piese');
    makeBarChart('chart-parts-category', partsCategory.slice(0, 12).map((r: { category: string }) => r.category), partsCategory.slice(0, 12).map((r: Numar) => r.cnt), 'Piese', true);
    makeBarChart('chart-errors-machine', errorsMachine.map((r: { machine_id: string }) => r.machine_id), errorsMachine.map((r: Numar) => r.cnt), 'Coduri eroare');
    makeDoughnutChart('chart-machines-type', machinesType.map((r: { machine_type: string }) => r.machine_type), machinesType.map((r: Numar) => r.cnt));

    makeStackedBar('chart-intervals-machine',
        intervalsMachine.map((r: { machine_id: string }) => r.machine_id),
        [
            { label: 'Non-critice', data: intervalsMachine.map((r: Numar & { critical_cnt: number | null }) => r.cnt - (r.critical_cnt || 0)), bg: '#3b82f6' },
            { label: 'Critice', data: intervalsMachine.map((r: { critical_cnt: number | null }) => r.critical_cnt || 0), bg: '#ef4444' }
        ]
    );

    makeDoughnutChart('chart-wear-type', partsWear.map((r: { wear_type: string }) => r.wear_type === 'U' ? 'Uzură (U)' : r.wear_type === 'S' ? 'Stoc (S)' : 'N/A'), partsWear.map((r: Numar) => r.cnt));
    makeBarChart('chart-docs-type', docsType.map((r: { doc_type: string }) => r.doc_type), docsType.map((r: Numar) => r.cnt), 'Documente');
    // categoria de piese din care se citeau materialele nu mai există (0 rânduri) — un grafic gol
    // sub titlul „Materiale Matrițe" spunea că n-avem nimic; fără date, cardul nu se arată
    const cardMat = document.getElementById('chart-mold-materials')?.closest('.chart-card') as HTMLElement | null;
    if (cardMat) cardMat.hidden = !(moldMaterials && moldMaterials.length);
    if (moldMaterials && moldMaterials.length) {
        makeDoughnutChart('chart-mold-materials', moldMaterials.slice(0, 10).map((r: { material: string }) => r.material), moldMaterials.slice(0, 10).map((r: Numar) => r.cnt));
    }

    // Statistici operaționale (regândit 2 aug 2026): ce SE ÎNTÂMPLĂ în fabrică,
    // nu ce am importat în catalog. Cardurile + 6 grafice pe ultimele 6 luni.
    try {
        const mnt: StatMentenanta = await api('/api/stats/mentenanta');
        const luni = (mnt.pe_luna || []).map((r) => r.luna);
        makeStackedBar('chart-mnt-luna', luni, [
            { label: 'Urgențe', data: (mnt.pe_luna || []).map((r) => r.urgente || 0), bg: '#ef4444' },
            { label: 'Planificate', data: (mnt.pe_luna || []).map((r) => r.planificate || 0), bg: '#3b82f6' },
        ]);
        makeBarChart('chart-mnt-mttr', luni, (mnt.pe_luna || []).map((r) => r.mttr_min || 0), 'MTTR (min)');
        makeBarChart('chart-mnt-top',
            (mnt.top_masini_urgente || []).map((r) => r.machine_id),
            (mnt.top_masini_urgente || []).map((r) => r.cnt), 'Urgențe', true);

        const lunAl = (mnt.alarme_pe_luna || []).map((r) => r.luna);
        makeStackedBar('chart-alarme-luna', lunAl, [
            { label: 'Critice', data: (mnt.alarme_pe_luna || []).map((r) => r.critice || 0), bg: '#ef4444' },
            { label: 'Restul (warning/info)', data: (mnt.alarme_pe_luna || []).map((r) => (r.total || 0) - (r.critice || 0)), bg: '#f59e0b' },
        ]);
        const lunLg = (mnt.legate_pe_luna || []).map((r) => r.luna);
        makeStackedBar('chart-legate-luna', lunLg, [
            { label: 'Legate de interval (din Calendar)', data: (mnt.legate_pe_luna || []).map((r) => r.legate || 0), bg: '#22c55e' },
            { label: 'Libere (nelegate)', data: (mnt.legate_pe_luna || []).map((r) => r.libere || 0), bg: '#6b7280' },
        ]);
        makeBarChart('chart-ore-linii',
            (mnt.ore_linii || []).map((r) => r.id),
            (mnt.ore_linii || []).map((r) => r.ore || 0), 'Ore funcționare');

        // cardurile operaționale: luna curentă dintr-o privire
        const cardsEl = document.getElementById('stat-op-cards');
        if (cardsEl) {
            // luna CURENTĂ, nu ultimul rând primit: pe 1 ale lunii, până la prima lucrare, ultimul
            // rând e luna trecută — cardurile „luna curentă" arătau cifrele lunii precedente
            const _az = new Date();
            const lunaAzi = _az.getFullYear() + '-' + String(_az.getMonth() + 1).padStart(2, '0');
            const ult = <T extends StatLuna>(arr: T[]): Partial<T> => ((arr || []).find((r) => r && r.luna === lunaAzi) || {});
            const lc = ult(mnt.pe_luna);
            const la = ult(mnt.alarme_pe_luna);
            const lg = ult(mnt.legate_pe_luna);
            const lp = ult(mnt.pov_pe_luna);
            const totalLg = (lg.legate || 0) + (lg.libere || 0);
            const pctLegate = totalLg ? Math.round((lg.legate || 0) * 100 / totalLg) : 0;
            let compliance = '—';
            try {
                const c = await api('/api/reports/compliance');
                if (c?.summary?.avg_compliance != null) compliance = c.summary.avg_compliance + '%';
            } catch (_e) { /* compliance e engineer+; cardul rămâne — */ }
            cardsEl.innerHTML = `
                <div class="stat-card red"><div class="label">🚨 Urgențe luna curentă</div><div class="value">${lc.urgente ?? 0}</div></div>
                <div class="stat-card"><div class="label">⏱ MTTR luna curentă</div><div class="value">${lc.mttr_min != null ? lc.mttr_min + ' min' : '—'}</div></div>
                <div class="stat-card orange"><div class="label">🔔 Alarme luna curentă</div><div class="value">${la.total ?? 0}<span style="font-size:.6em;color:var(--red)"> (${la.critice ?? 0} critice)</span></div></div>
                <div class="stat-card ${pctLegate >= 50 ? 'green' : ''}"><div class="label">🔗 Lucrări din Calendar</div><div class="value">${pctLegate}%</div></div>
                <div class="stat-card green"><div class="label">🎥 Filmări POV luna</div><div class="value">${lp.filmari ?? 0}</div></div>
                <div class="stat-card"><div class="label">📋 Compliance luna curentă</div><div class="value">${compliance}</div></div>
            `;
        }
    } catch (_e) { /* KPI opțional — nu blocăm restul statisticilor */ }
}

export function makeBarChart(canvasId: string, labels: (string | number | null)[], data: (number | null)[], label: string, horizontal = false) {
    destroyChart(canvasId);
    const ctx = document.getElementById(canvasId);
    chartInstances[canvasId] = new Chart(ctx, {
        type: 'bar',
        data: {
            labels,
            datasets: [{
                label,
                data,
                backgroundColor: CHART_COLORS.slice(0, data.length),
                borderRadius: 4,
                borderSkipped: false,
            }]
        },
        options: {
            indexAxis: horizontal ? 'y' : 'x',
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: { label: (ctx: { parsed: Record<'x' | 'y', number> }) => `${ctx.parsed[horizontal ? 'x' : 'y'].toLocaleString()} ${label.toLowerCase()}` }
                }
            },
            scales: {
                x: { grid: { display: !horizontal }, ticks: { font: { size: 11 } } },
                y: { grid: { display: horizontal }, ticks: { font: { size: 11 } }, beginAtZero: true }
            }
        }
    });
}

export function makeDoughnutChart(canvasId: string, labels: (string | number | null)[], data: (number | null)[]) {
    destroyChart(canvasId);
    const ctx = document.getElementById(canvasId);
    chartInstances[canvasId] = new Chart(ctx, {
        type: 'doughnut',
        data: {
            labels,
            datasets: [{
                data,
                backgroundColor: CHART_COLORS.slice(0, data.length),
                borderWidth: 2,
                borderColor: '#111520',
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
                legend: { position: 'right', labels: { font: { size: 11 }, padding: 8 } },
            },
            cutout: '55%'
        }
    });
}

export function makeStackedBar(canvasId: string, labels: (string | number | null)[], datasets: SerieGrafic[]) {
    destroyChart(canvasId);
    const ctx = document.getElementById(canvasId);
    chartInstances[canvasId] = new Chart(ctx, {
        type: 'bar',
        data: {
            labels,
            datasets: datasets.map((ds) => ({
                label: ds.label,
                data: ds.data,
                backgroundColor: ds.bg,
                borderRadius: 4,
            }))
        },
        options: {
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
                legend: { position: 'top', labels: { font: { size: 11 } } },
            },
            scales: {
                x: { stacked: true, ticks: { font: { size: 11 } } },
                y: { stacked: true, beginAtZero: true, ticks: { font: { size: 11 } } }
            }
        }
    });
}
